import { createFileRoute, Link } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { BarChartHoverLabel } from '@/components/admin/BarChartHoverLabel';
import { apiJson } from '@/lib/api';
import { track } from '@/lib/analytics';
import type {
  YearlyTrialChartRange,
  YearlyTrialDailyPoint,
  YearlyTrialMetricsResponse,
} from '@/lib/types';

const RANGE_OPTIONS: { id: YearlyTrialChartRange; label: string }[] = [
  { id: '7d', label: '7d' },
  { id: '30d', label: '30d' },
  { id: 'all', label: 'All' },
];

const VARIANT_LABELS: Record<string, string> = {
  already_granted: 'Already granted',
  never_granted: 'Never granted',
};

const OUTCOME_LABELS: Record<string, string> = {
  no_action: 'No action',
  started: 'Started',
  trialing: 'Trialing',
  paid: 'Paid',
};

function formatCount(value: number): string {
  return value.toLocaleString();
}

function formatPercent(rate: number | null | undefined): string {
  if (rate == null) return '—';
  const pct = rate * 100;
  return `${pct.toLocaleString(undefined, {
    minimumFractionDigits: pct === 0 || pct >= 10 ? 0 : 1,
    maximumFractionDigits: 1,
  })}%`;
}

function formatVsBenchmark(delta: number | null | undefined): string {
  if (delta == null) return '—';
  const pct = delta * 100;
  const sign = pct > 0 ? '+' : '';
  return `${sign}${pct.toFixed(1)} pp`;
}

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  const dt = new Date(value.endsWith('Z') ? value : `${value}Z`);
  if (Number.isNaN(dt.getTime())) return value;
  return dt.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDateLabel(ymd: string): string {
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return ymd;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
  });
}

function StatTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
      <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
        {label}
      </p>
      <p className='mt-1 text-2xl font-semibold tabular-nums text-gray-900'>
        {value}
      </p>
      {hint ? <p className='mt-1 text-xs text-gray-500'>{hint}</p> : null}
    </div>
  );
}

function FunnelBars({
  sent,
  started,
  paid,
}: {
  sent: number;
  started: number;
  paid: number;
}) {
  const max = Math.max(sent, 1);
  const steps = [
    { label: 'Sent', value: sent, color: '#018A9D' },
    { label: 'Started', value: started, color: '#0f766e' },
    { label: 'Paid', value: paid, color: '#f59e0b' },
  ];
  return (
    <div className='space-y-3'>
      {steps.map((step) => (
        <div key={step.label}>
          <div className='mb-1 flex items-center justify-between text-sm'>
            <span className='font-medium text-gray-700'>{step.label}</span>
            <span className='tabular-nums text-gray-900'>
              {formatCount(step.value)}
            </span>
          </div>
          <div className='h-3 overflow-hidden rounded-full bg-gray-100'>
            <div
              className='h-full rounded-full'
              style={{
                width: `${Math.max((step.value / max) * 100, step.value > 0 ? 2 : 0)}%`,
                backgroundColor: step.color,
              }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function CumulativeChart({
  points,
}: {
  points: YearlyTrialDailyPoint[];
}) {
  const [hovered, setHovered] = useState<number | null>(null);
  if (!points.length) {
    return (
      <p className='text-sm text-gray-500'>No sends yet. Chart starts after Wave 1 goes out.</p>
    );
  }

  const width = 720;
  const height = 220;
  const padX = 36;
  const padY = 24;
  const innerW = width - padX * 2;
  const innerH = height - padY * 2;
  const n = points.length;
  const max = Math.max(1, ...points.map((p) => Math.max(p.sent, p.started, p.paid)));
  const series = [
    { key: 'sent', color: '#018A9D', label: 'Sent' },
    { key: 'started', color: '#0f766e', label: 'Started' },
    { key: 'paid', color: '#f59e0b', label: 'Paid' },
  ] as const;

  const hoverPoint = hovered != null ? points[hovered] : null;
  const hoverX =
    hovered != null ? padX + (n === 1 ? innerW / 2 : (hovered / (n - 1)) * innerW) : 0;
  const hoverY =
    hoverPoint != null
      ? height - padY - (hoverPoint.sent / max) * innerH
      : padY;

  return (
    <div className='overflow-x-auto' onMouseLeave={() => setHovered(null)}>
      <svg viewBox={`0 0 ${width} ${height}`} className='h-56 w-full min-w-[28rem]'>
        {series.map((s) => {
          const d = points
            .map((p, i) => {
              const x = n === 1 ? padX + innerW / 2 : padX + (i / (n - 1)) * innerW;
              const y = height - padY - (p[s.key] / max) * innerH;
              return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
            })
            .join(' ');
          return (
            <path
              key={s.key}
              d={d}
              fill='none'
              stroke={s.color}
              strokeWidth={2}
            />
          );
        })}
        {points.map((p, i) => {
          const x = n === 1 ? padX + innerW / 2 : padX + (i / (n - 1)) * innerW;
          const barW = Math.max(innerW / Math.max(n, 1) - 4, 2);
          return (
            <rect
              key={p.date}
              x={x - barW / 2}
              y={padY}
              width={barW}
              height={innerH}
              fill='transparent'
              onMouseEnter={() => setHovered(i)}
            />
          );
        })}
        {hoverPoint ? (
          <BarChartHoverLabel
            x={hoverX}
            barTopY={hoverY}
            minX={padX + 40}
            maxX={width - padX - 40}
            text={`${formatDateLabel(hoverPoint.date)} · ${hoverPoint.sent} sent · ${hoverPoint.started} started · ${hoverPoint.paid} paid`}
          />
        ) : null}
      </svg>
      <div className='mt-2 flex flex-wrap gap-4 text-xs text-gray-600'>
        {series.map((s) => (
          <span key={s.key} className='inline-flex items-center gap-1.5'>
            <span
              className='h-2 w-2 rounded-full'
              style={{ backgroundColor: s.color }}
              aria-hidden
            />
            {s.label}
          </span>
        ))}
      </div>
    </div>
  );
}

function AdminYearlyTrialPage() {
  const { getToken } = useAuth();
  const [range, setRange] = useState<YearlyTrialChartRange>('all');
  const [outcomeFilter, setOutcomeFilter] = useState<string>('all');
  const [variantFilter, setVariantFilter] = useState<string>('all');
  const [sortKey, setSortKey] = useState<'sent_at' | 'outcome'>('sent_at');
  const [funnelVariant, setFunnelVariant] = useState<string>('all');

  useEffect(() => {
    track('Yearly Trial Backfill Screen');
  }, []);

  const metricsQuery = useQuery({
    queryKey: ['admin', 'yearly-trial-backfill', range],
    queryFn: () =>
      apiJson<YearlyTrialMetricsResponse>(
        getToken,
        `/v1/admin/yearly-trial-backfill/metrics?range=${encodeURIComponent(range)}`,
      ),
  });

  const data = metricsQuery.data;
  const leftover = data?.leftover;
  const kpis = data?.kpis;

  const filteredRecipients = useMemo(() => {
    const rows = data?.recipients ?? [];
    const filtered = rows.filter((row) => {
      if (outcomeFilter !== 'all' && row.outcome !== outcomeFilter) return false;
      if (variantFilter !== 'all' && row.variant !== variantFilter) return false;
      return true;
    });
    return [...filtered].sort((a, b) => {
      if (sortKey === 'outcome') {
        return a.outcome.localeCompare(b.outcome) || a.sent_at.localeCompare(b.sent_at);
      }
      return b.sent_at.localeCompare(a.sent_at);
    });
  }, [data?.recipients, outcomeFilter, variantFilter, sortKey]);

  const funnelSource = useMemo(() => {
    if (!data) return { sent: 0, started: 0, paid: 0 };
    if (funnelVariant === 'all') {
      return {
        sent: data.kpis.sent,
        started: data.kpis.started,
        paid: data.kpis.paid,
      };
    }
    const row = data.variants.find((v) => v.variant === funnelVariant);
    return {
      sent: row?.sent ?? 0,
      started: row?.started ?? 0,
      paid: row?.paid ?? 0,
    };
  }, [data, funnelVariant]);

  return (
    <div className='space-y-6'>
      <div>
        <h1 className='text-2xl font-bold text-gray-900'>7-Day Trial</h1>
        <p className='text-sm text-gray-500'>
          Wave 1 letter about the store yearly trial. Tracking pixels are off on purpose.
        </p>
      </div>

      {metricsQuery.isLoading ? (
        <div className='flex justify-center py-16'>
          <div className='h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent' />
        </div>
      ) : null}

      {metricsQuery.isError ? (
        <p className='text-sm text-rose-700'>Could not load yearly trial metrics.</p>
      ) : null}

      {data ? (
        <>
          <div className='grid gap-3 sm:grid-cols-2 lg:grid-cols-4'>
            <StatTile
              label='Campaign'
              value={data.enabled ? (data.hold_active ? 'Held' : 'Enabled') : 'Disabled'}
              hint={
                data.held_until
                  ? `Hold until ${formatDateTime(data.held_until)} UTC`
                  : 'No send hold'
              }
            />
            <StatTile
              label='Wave 1 sent'
              value={formatCount(data.wave1_sent)}
              hint={`${formatCount(data.wave1_remaining)} remaining eligible`}
            />
            <StatTile
              label='Last send'
              value={formatDateTime(data.last_sent_at)}
            />
            <StatTile
              label='SCT benchmark'
              value={formatPercent(data.kpis.sct_benchmark_rate)}
              hint='Second-chance was 12 / 523 = 2.3%'
            />
          </div>

          <div className='grid gap-3 sm:grid-cols-2 lg:grid-cols-5'>
            <StatTile label='Sent' value={formatCount(kpis?.sent ?? 0)} />
            <StatTile
              label='Started'
              value={formatCount(kpis?.started ?? 0)}
              hint={`Start rate ${formatPercent(kpis?.start_rate)} vs 2.3%`}
            />
            <StatTile
              label='Paid'
              value={formatCount(kpis?.paid ?? 0)}
              hint={`Paid rate ${formatPercent(kpis?.paid_rate)} vs 2.3%`}
            />
            <StatTile
              label='Start vs SCT'
              value={formatVsBenchmark(
                kpis?.start_rate == null
                  ? null
                  : kpis.start_rate - kpis.sct_benchmark_rate,
              )}
            />
            <StatTile label='No action' value={formatCount(kpis?.no_action ?? 0)} />
          </div>

          <div className='grid gap-4 lg:grid-cols-2'>
            <section className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
              <div className='mb-3 flex items-center justify-between gap-2'>
                <h2 className='text-sm font-semibold text-gray-900'>Funnel</h2>
                <select
                  value={funnelVariant}
                  onChange={(e) => setFunnelVariant(e.target.value)}
                  className='rounded-md border border-gray-300 bg-white px-2 py-1 text-xs'
                >
                  <option value='all'>All variants</option>
                  <option value='already_granted'>Already granted</option>
                  <option value='never_granted'>Never granted</option>
                </select>
              </div>
              <FunnelBars
                sent={funnelSource.sent}
                started={funnelSource.started}
                paid={funnelSource.paid}
              />
            </section>

            <section className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
              <div className='mb-3 flex items-center justify-between gap-2'>
                <h2 className='text-sm font-semibold text-gray-900'>
                  Cumulative
                </h2>
                <div className='flex gap-1'>
                  {RANGE_OPTIONS.map((opt) => (
                    <button
                      key={opt.id}
                      type='button'
                      onClick={() => setRange(opt.id)}
                      className={`rounded-md px-2 py-1 text-xs font-medium ${
                        range === opt.id
                          ? 'bg-harbor-50 text-primary'
                          : 'text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <CumulativeChart points={data.daily} />
            </section>
          </div>

          <section className='overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm'>
            <div className='border-b border-gray-100 px-4 py-3'>
              <h2 className='text-sm font-semibold text-gray-900'>Variants</h2>
            </div>
            <table className='min-w-full text-sm'>
              <thead className='bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500'>
                <tr>
                  <th className='px-4 py-2'>Variant</th>
                  <th className='px-4 py-2 text-right'>Sent</th>
                  <th className='px-4 py-2 text-right'>Started</th>
                  <th className='px-4 py-2 text-right'>Paid</th>
                  <th className='px-4 py-2 text-right'>Start %</th>
                  <th className='px-4 py-2 text-right'>Paid %</th>
                  <th className='px-4 py-2 text-right'>vs 2.3%</th>
                </tr>
              </thead>
              <tbody>
                {data.variants.map((row) => (
                  <tr key={row.variant} className='border-t border-gray-100'>
                    <td className='px-4 py-2'>{VARIANT_LABELS[row.variant] ?? row.variant}</td>
                    <td className='px-4 py-2 text-right tabular-nums'>{formatCount(row.sent)}</td>
                    <td className='px-4 py-2 text-right tabular-nums'>{formatCount(row.started)}</td>
                    <td className='px-4 py-2 text-right tabular-nums'>{formatCount(row.paid)}</td>
                    <td className='px-4 py-2 text-right tabular-nums'>{formatPercent(row.start_rate)}</td>
                    <td className='px-4 py-2 text-right tabular-nums'>{formatPercent(row.paid_rate)}</td>
                    <td className='px-4 py-2 text-right tabular-nums'>{formatVsBenchmark(row.vs_sct_benchmark)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          {leftover ? (
            <section className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
              <h2 className='text-sm font-semibold text-gray-900'>Audience leftover</h2>
              <p className='mt-1 text-xs text-gray-500'>
                Live waterfall of never-subscribed users with email. Same rules as the send.
              </p>
              <dl className='mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4'>
                <div>
                  <dt className='text-xs uppercase tracking-wide text-gray-500'>Wave 1 remaining</dt>
                  <dd className='text-lg font-semibold tabular-nums'>{formatCount(leftover.wave1_remaining)}</dd>
                </div>
                <div>
                  <dt className='text-xs uppercase tracking-wide text-gray-500'>Wave 2</dt>
                  <dd className='text-lg font-semibold tabular-nums'>{formatCount(leftover.wave2_total)}</dd>
                  <p className='text-xs text-gray-500'>
                    {formatCount(leftover.wave2_already_granted)} already /{' '}
                    {formatCount(leftover.wave2_never_granted)} never
                  </p>
                </div>
                <div>
                  <dt className='text-xs uppercase tracking-wide text-gray-500'>Hold cold</dt>
                  <dd className='text-lg font-semibold tabular-nums'>{formatCount(leftover.hold_cold)}</dd>
                </div>
                <div>
                  <dt className='text-xs uppercase tracking-wide text-gray-500'>Excluded</dt>
                  <dd className='text-lg font-semibold tabular-nums'>{formatCount(leftover.excluded_total)}</dd>
                  <p className='text-xs text-gray-500'>
                    {formatCount(leftover.excluded_professionals)} pro ·{' '}
                    {formatCount(leftover.excluded_unsub_or_suppressed)} unsub ·{' '}
                    {formatCount(leftover.excluded_store_history)} store ·{' '}
                    {formatCount(leftover.excluded_activation_window)} activation
                  </p>
                </div>
              </dl>
            </section>
          ) : null}

          <section className='overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm'>
            <div className='flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-3'>
              <h2 className='text-sm font-semibold text-gray-900'>Recipients</h2>
              <div className='flex flex-wrap gap-2'>
                <select
                  value={outcomeFilter}
                  onChange={(e) => setOutcomeFilter(e.target.value)}
                  className='rounded-md border border-gray-300 bg-white px-2 py-1 text-xs'
                >
                  <option value='all'>All outcomes</option>
                  {Object.entries(OUTCOME_LABELS).map(([id, label]) => (
                    <option key={id} value={id}>
                      {label}
                    </option>
                  ))}
                </select>
                <select
                  value={variantFilter}
                  onChange={(e) => setVariantFilter(e.target.value)}
                  className='rounded-md border border-gray-300 bg-white px-2 py-1 text-xs'
                >
                  <option value='all'>All variants</option>
                  <option value='already_granted'>Already granted</option>
                  <option value='never_granted'>Never granted</option>
                </select>
                <select
                  value={sortKey}
                  onChange={(e) => setSortKey(e.target.value as 'sent_at' | 'outcome')}
                  className='rounded-md border border-gray-300 bg-white px-2 py-1 text-xs'
                >
                  <option value='sent_at'>Sort by sent</option>
                  <option value='outcome'>Sort by outcome</option>
                </select>
              </div>
            </div>
            <table className='min-w-full text-sm'>
              <thead className='bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500'>
                <tr>
                  <th className='px-4 py-2'>Name</th>
                  <th className='px-4 py-2'>Email</th>
                  <th className='px-4 py-2'>Variant</th>
                  <th className='px-4 py-2'>Sent</th>
                  <th className='px-4 py-2'>Outcome</th>
                  <th className='px-4 py-2 text-right'>Days</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecipients.length === 0 ? (
                  <tr>
                    <td className='px-4 py-6 text-sm text-gray-500' colSpan={6}>
                      No recipients yet.
                    </td>
                  </tr>
                ) : (
                  filteredRecipients.map((row) => (
                    <tr key={`${row.user_id}-${row.sent_at}`} className='border-t border-gray-100'>
                      <td className='px-4 py-2'>
                        <Link
                          to='/admin/users/$userId'
                          params={{ userId: String(row.user_id) }}
                          search={{ hid: undefined }}
                          className='font-medium text-primary hover:underline'
                        >
                          {[row.first_name, row.last_name].filter(Boolean).join(' ') || `User ${row.user_id}`}
                        </Link>
                      </td>
                      <td className='px-4 py-2 text-gray-600'>{row.email}</td>
                      <td className='px-4 py-2'>{VARIANT_LABELS[row.variant] ?? row.variant}</td>
                      <td className='px-4 py-2 tabular-nums text-gray-600'>
                        {formatDateTime(row.sent_at)}
                      </td>
                      <td className='px-4 py-2'>{OUTCOME_LABELS[row.outcome] ?? row.outcome}</td>
                      <td className='px-4 py-2 text-right tabular-nums'>{row.days_since_send}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </section>
        </>
      ) : null}
    </div>
  );
}

export const Route = createFileRoute('/admin/yearly-trial')({
  component: AdminYearlyTrialPage,
});
